import { Canvas, useFrame } from "@react-three/fiber";
import { Float, Points, PointMaterial, Icosahedron } from "@react-three/drei";
import { useMemo, useRef, Suspense } from "react";
import * as THREE from "three";

function ParticleField({ count = 900 }) {
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 4 + Math.random() * 4;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      arr[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      arr[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      arr[i * 3 + 2] = r * Math.cos(phi);
    }
    return arr;
  }, [count]);

  const ref = useRef();
  useFrame((_, delta) => {
    if (ref.current) {
      ref.current.rotation.y += delta * 0.04;
      ref.current.rotation.x += delta * 0.02;
    }
  });

  return (
    <Points ref={ref} positions={positions} stride={3} frustumCulled={false}>
      <PointMaterial
        transparent
        color="#CCFF00"
        size={0.018}
        sizeAttenuation
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </Points>
  );
}

function CyanRing() {
  const ref = useRef();
  useFrame((_, delta) => {
    if (ref.current) {
      ref.current.rotation.x += delta * 0.15;
      ref.current.rotation.z += delta * 0.08;
    }
  });
  return (
    <mesh ref={ref}>
      <torusGeometry args={[2.6, 0.006, 16, 200]} />
      <meshBasicMaterial color="#00F0FF" transparent opacity={0.55} />
    </mesh>
  );
}

function Monolith() {
  const meshRef = useRef();
  useFrame((state, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += delta * 0.25;
      meshRef.current.rotation.x =
        Math.sin(state.clock.elapsedTime * 0.4) * 0.2;
    }
  });
  return (
    <Float speed={1.1} rotationIntensity={0.35} floatIntensity={0.7}>
      <Icosahedron ref={meshRef} args={[1.25, 1]}>
        <meshStandardMaterial
          color="#0A0A0B"
          roughness={0.35}
          metalness={0.85}
          emissive="#000000"
        />
      </Icosahedron>
      {/* Wireframe overlay */}
      <Icosahedron args={[1.28, 2]}>
        <meshBasicMaterial color="#CCFF00" wireframe transparent opacity={0.35} />
      </Icosahedron>
    </Float>
  );
}

export default function NeuralMonolith() {
  return (
    <Canvas
      camera={{ position: [0, 0, 5], fov: 55 }}
      dpr={[1, 1.6]}
      gl={{ antialias: true, alpha: true }}
      style={{ background: "transparent" }}
    >
      <Suspense fallback={null}>
        <ambientLight intensity={0.25} />
        <directionalLight position={[6, 6, 6]} intensity={1.4} color="#CCFF00" />
        <pointLight position={[-6, -4, 4]} intensity={1.2} color="#00F0FF" />
        <pointLight position={[0, 0, -4]} intensity={0.6} color="#FFFFFF" />
        <Monolith />
        <CyanRing />
        <ParticleField />
      </Suspense>
    </Canvas>
  );
}